// Pixel/ms trajectories with a fixed arrival time. A new swipe can redirect a
// moving camera without dropping its velocity to zero for another ease-in.
export function createScrollTrajectory(start, end, duration, velocity = 0, maxPosition = Infinity) {
  const distance = end - start;
  const still = () => ({ position: end, velocity: 0 });
  if (duration <= 0 || (Math.abs(distance) < .000001 && Math.abs(velocity) < .000001)) return still;
  if (velocity && velocity * distance <= 0) {
    const room = velocity > 0 ? maxPosition - start : start;
    const braking = Math.min(120, duration * .15, 3 * Math.max(0, room) / Math.abs(velocity));
    if (braking > 0) {
      const turn = start + velocity * braking / 3;
      const finish = createScrollTrajectory(turn, end, duration - braking, 0, maxPosition);
      return elapsed => {
        if (elapsed >= braking) return finish(elapsed - braking);
        const t = Math.max(0, elapsed / braking);
        return {
          position: start + velocity * braking * (t - t * t + t * t * t / 3),
          velocity: velocity * (1 - t) ** 2,
        };
      };
    }
    velocity = 0;
  }
  const slope = distance ? velocity * duration / distance : 0;
  return elapsed => {
    const t = Math.max(0, Math.min(1, elapsed / duration));
    if (t === 1) return still();
    if (!velocity) return {
      position: start + distance * t ** 3 * (t * (t * 6 - 15) + 10),
      velocity: distance * 30 * t ** 2 * (1 - t) ** 2 / duration,
    };
    if (slope > 3) return {
      // Preserve a fast incoming velocity without overshooting a near target.
      position: start + distance * (1 - (1 - t) ** slope),
      velocity: velocity * (1 - t) ** (slope - 1),
    };
    const tangent = velocity * duration;
    return {
      position: start + distance * t * t * (3 - 2 * t) + tangent * t * (1 - t) ** 2,
      velocity: (1 - t) * (tangent + (6 * distance - 3 * tangent) * t) / duration,
    };
  };
}

// Pass a title at a low, nonzero speed, then ease into its content.
export function createTitleTravel(start, title, end, duration, velocity = 0) {
  const direction = Math.sign(end - start);
  const firstDuration = duration * .58;
  const secondDuration = duration - firstDuration;
  const passSpeed = direction * Math.min(Math.abs(title - start) / firstDuration, Math.abs(end - title) / secondDuration) * .35;
  function segment(a, b, ms, v0, v1, elapsed) {
    const t = Math.max(0, Math.min(1, elapsed / ms));
    return {
      position: (2*t**3-3*t*t+1)*a + (t**3-2*t*t+t)*ms*v0 + (-2*t**3+3*t*t)*b + (t**3-t*t)*ms*v1,
      velocity: ((6*t*t-6*t)*a + (3*t*t-4*t+1)*ms*v0 + (-6*t*t+6*t)*b + (3*t*t-2*t)*ms*v1)/ms,
    };
  }
  return elapsed => elapsed < firstDuration
    ? segment(start, title, firstDuration, velocity, passSpeed, elapsed)
    : segment(title, end, secondDuration, passSpeed, 0, elapsed-firstDuration);
}
